import { Button } from '@ts/components/showcase-ui/Button/Button';
import { TargetedEvent, useRef } from 'preact/compat';
import { useState } from 'preact/hooks';
import { allowedTime, isCommentAllowed } from './comment';
import { InfoBanner } from '../InfoBanner/InfoBanner';
import { StateUpdaterProps } from './formTypes';

type Props = {
  id: string;
  commentId?: string;
  replyForm?: HTMLDivElement;
  stateUpdater: StateUpdaterProps;
  confirmButton: HTMLElement;
};

export const CommentFormFooter = ({
  id,
  commentId,
  replyForm,
  stateUpdater,
  confirmButton,
}: Props) => {
  const footerRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLDivElement>(null);
  const charcountRef = useRef<HTMLSpanElement>(null);
  const feedbackRef = useRef<HTMLDivElement>(null);
  const [showErrorBannerReply, setShowErrorBannerReply] = useState<boolean>();

  const isReplyComment = !!commentId;

  const getEditor = () => {
    const formId = commentId
      ? `#formfield-reply-comment-text-${commentId}`
      : '#formfield-form-widgets-comment-text';

    const base = replyForm || document;

    // Get the most updated comment text
    const form = base.querySelector<HTMLElement>(formId);

    return form;
  };

  const handleConfirmComment = (evt: TargetedEvent) => {
    evt.preventDefault();

    // Is comment allowed by time
    if (!isCommentAllowed()) {
      if (isReplyComment) setShowErrorBannerReply(() => true);

      if (stateUpdater.setShowErrorBanner)
        stateUpdater.setShowErrorBanner(() => true);

      (evt.target as HTMLElement)?.classList.add('bo-btn--disabled');
      return;
    }

    // Early return if button had disabled class so click event is not triggered and comment is not saved
    if (
      (evt.target as HTMLElement).firstElementChild?.classList.contains(
        'bo-btn--disabled',
      )
    )
      return;

    const commentHTML =
      getEditor()?.getElementsByTagName('iframe').item(0)?.contentDocument?.body
        .innerHTML || '';

    stateUpdater.setCommentHTMLStr(() => commentHTML);

    // Open Modal
    stateUpdater.setIsModalOpen(true);

    // Set correct confirm button
    stateUpdater.setConfirmButton(() => confirmButton);
  };

  const handleCancelComment = (evt: TargetedEvent) => {
    evt.preventDefault();

    const commentText = getEditor()?.getElementsByTagName('iframe').item(0)
      ?.contentDocument?.body;
    if (!commentText) return;

    const form = getEditor()?.parentElement;
    if (form?.id === 'reply-to-comment-form') {
      const replyButton =
        form?.parentElement?.previousElementSibling?.firstElementChild;
      if (!replyButton) return;

      // Replace reply form for reply initial button
      (replyButton as HTMLElement).style.display = 'inline-block'; // Display reply button
      form.parentElement.remove(); // Remove form container tag
    } else {
      commentText.innerHTML = '<p><br data-mce-bogus="1"></p>'; // Empty the comment box

      confirmButtonRef.current?.firstElementChild?.classList.add(
        'bo-btn--disabled',
      ); // Disable comment button itinially

      const charcount = charcountRef.current;
      const editor = form?.querySelector<HTMLElement>('.tox-tinymce');
      if (!charcount || !editor) return;

      // Fix charcount and editor back to initial state
      charcount.innerHTML = '0';
      charcount.classList.remove('bc-comments__feedback--error');
      editor.style.height = '100px';

      if (feedbackRef.current) feedbackRef.current.style.display = 'none';
    }
  };

  return (
    <>
      <div className="bc-comment-form__footer" ref={footerRef}>
        <div
          className={`bc-comments__feedback--error js-message-charcount-${id}`}
          style={{
            display: 'none',
            width: '100%',
            marginTop: 'var(--DF-PRIM-SPACING-8)',
          }}
          ref={feedbackRef}
        >
          Aktuell überschreitet Ihr Kommentar die maximale Länge von 1.000
          Zeichen
        </div>

        <div className="bc-comment-form__footer-content">
          <div className="bc-comment-form__buttons">
            <div
              onClick={evt => handleConfirmComment(evt)}
              ref={confirmButtonRef}
            >
              <Button
                label={!isReplyComment ? 'Kommentieren' : 'Antworten'}
                className={`bo-btn--md bo-btn--secondary js-modal-button-${id} bo-btn--disabled`}
                type="button"
              />
            </div>

            <div
              onClick={evt => {
                handleCancelComment(evt);
              }}
            >
              <Button
                label="Abbrechen"
                className={`bo-btn--md bo-btn--underline`}
                type="button"
              />
            </div>
          </div>

          <div className="bc-comments__feedback--count">
            <span
              className={`bc-comments__feedback--charcount js-character-count-${id}`}
              ref={charcountRef}
            >
              0
            </span>
            /1000
          </div>
        </div>

        {showErrorBannerReply && (
          <InfoBanner
            type="error"
            content={`Die Kommentarfunktion ist derzeit geschlossen. Sie können jeden Tag zwischen ${allowedTime.startTime}:00 und ${allowedTime.endTime}:00 kommentieren.`}
            marginTop="var(--DF-PRIM-SPACING-24)"
          />
        )}
      </div>
    </>
  );
};
